import type { Ref } from "vue";
import type { Requests } from "../common/request.common";
import type { SubscriptionResponseTypes } from "../common/response.common";
import type { SelectParam } from "../common/types.common";
import { asRemoveHook, asUpdateHook, buildSelectParam, get, post } from "./common";

export const subscription = {
    record(customerId?: string) {
        return (data: typeof Requests.Subscription.Record.Input) => {
            return post<typeof data, SubscriptionResponseTypes.Record>(
                `/subscription/record/${customerId ?? ""}`,
                data,
            );
        };
    },
    entries(data: typeof Requests.Subscription.Entries.Input) {
        return post<typeof data, SubscriptionResponseTypes.Entries>(`/subscription/entries`, data);
    },
    invoiceRecord(id: string) {
        return (data: typeof Requests.Subscription.InvoiceRecord.Input) => {
            return post<typeof data, SubscriptionResponseTypes.InvoiceRecord>(
                `/subscription/invoice-record/${id}`,
                data,
            );
        };
    },
    create(customerId: string, data: typeof Requests.Subscription.Create.Input) {
        if (import.meta.env.MODE === "production") throw new Error("INVALID ENDPOINT");
        return post<typeof data>(`/subscription/create/${customerId}`, data);
    },
    update(id: string, data: typeof Requests.Subscription.Update.Input) {
        return post<typeof data>(`/subscription/update/${id}`, data);
    },
    obtain(id: string) {
        return get<SubscriptionResponseTypes.Obtain>(`/subscription/obtain/${id}`);
    },
    formHooks: {
        selectFn(id: string | undefined) {
            return async (param?: SelectParam) => {
                if (id === undefined) {
                    return [];
                }

                const req = get<SubscriptionResponseTypes.Select>(
                    `/subscription/select/${id}` + buildSelectParam(param),
                );

                return (await req.execute()) ?? [];
            };
        },
        selectBroadbandFn(id: string | undefined) {
            return async (param?: SelectParam) => {
                if (id === undefined) {
                    return [];
                }

                const req = get<SubscriptionResponseTypes.SelectBroadband>(
                    `/subscription/select-broadband/${id}` + buildSelectParam(param),
                );

                return (await req.execute()) ?? [];
            };
        },
        update(id?: string, isLoading?: Ref<boolean>) {
            return async (data: typeof Requests.Subscription.Update.Input) => {
                if (id === undefined) return false;
                return await asUpdateHook(
                    post<typeof data>(`/subscription/update/${id}`, data),
                    isLoading,
                );
            };
        },
        updateAdmin(id: string, isLoading?: Ref<boolean>) {
            return async (data: typeof Requests.Subscription.UpdateAdmin.Input) => {
                return await asUpdateHook(
                    post<typeof data>(`/subscription/update-admin/${id}`, data),
                    isLoading,
                );
            };
        },
        remove(id?: string, isLoading?: Ref<boolean>) {
            return async (data: typeof Requests.Subscription.Remove.Input) => {
                if (id === undefined) return false;
                return await asRemoveHook(
                    post<typeof data>(`/subscription/remove/${id}`, data),
                    isLoading,
                );
            };
        },
        removeAdmin(id: string, isLoading?: Ref<boolean>) {
            return async (data: typeof Requests.Subscription.RemoveAdmin.Input) => {
                return await asRemoveHook(
                    post<typeof data>(`/subscription/remove-admin/${id}`, data),
                    isLoading,
                );
            };
        },
    },
};
